/**
 * platform-ai-page.tsx — Usage & chi phí AI theo tenant + khoá / trần của Platform.
 * Route `/platform/ai`. AI là chi phí biến đổi lớn nhất — cần biết org nào tiêu nhiều và chặn ngay tại đây.
 * Khung ba tầng: Platform ghi `platformAiDisabled` (khoá AI của tổ chức) và `platformMaxDaily` (trần lượt / ngày) —
 * tổ chức KHÔNG sửa được hai giá trị này; cài đặt riêng của tổ chức (bật AI, lượt / ngày) giữ nguyên, trần thực tế
 * = mức thấp hơn.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { formatNumber } from '@/lib/utils'
import { platformApiError } from '@/lib/platform-client'
import { usePlatformAiUsage, useSetAiQuota, type AiUsageItem } from '@/hooks/use-platform'

const usd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`

export function PlatformAiPage() {
  const [days, setDays] = useState(30)
  const { data, isLoading, isError } = usePlatformAiUsage(days)
  const setQuota = useSetAiQuota()
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const save = (orgId: string) => {
    const text = draft.trim()
    const n = text === '' ? null : Number(text)
    if (n !== null && (!Number.isInteger(n) || n < 0)) {
      toast.error('Trần phải là số nguyên không âm (để trống = không đặt)')
      return
    }
    setQuota.mutate(
      { orgId, platformMaxDaily: n },
      {
        onSuccess: () => { toast.success(n === null ? 'Đã bỏ trần của Platform' : 'Đã đặt trần lượt / ngày'); setEditing(null) },
        onError: (e) => toast.error(platformApiError(e)),
      },
    )
  }

  const toggleLock = (r: AiUsageItem) => {
    const lock = !r.platformAiDisabled
    if (lock && !window.confirm(`Khoá AI của "${r.orgName}"? Nhân viên AI của tổ chức ngừng trả lời và gợi ý; tổ chức không tự mở lại được.`)) return
    setQuota.mutate(
      { orgId: r.orgId, platformAiDisabled: lock },
      {
        onSuccess: () => toast.success(lock ? 'Đã khoá AI của tổ chức' : 'Đã mở khoá AI của tổ chức'),
        onError: (e) => toast.error(platformApiError(e)),
      },
    )
  }

  const columns: Column<AiUsageItem>[] = [
    { key: 'org', header: 'Tổ chức', cell: (r) => <span className="font-medium">{r.orgName}</span> },
    {
      key: 'cost', header: 'Chi phí', align: 'right',
      cell: (r) => <span className="font-semibold">{usd(r.costUsd)}</span>,
    },
    { key: 'calls', header: 'Lượt gọi', align: 'right', cell: (r) => formatNumber(r.calls) },
    {
      key: 'tokens', header: 'Token (vào/ra)', align: 'right',
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {formatNumber(r.tokensIn)} / {formatNumber(r.tokensOut)}
        </span>
      ),
    },
    {
      key: 'ai', header: 'AI của tổ chức',
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.aiEnabled === null ? <span className="text-muted-foreground">—</span>
            : r.aiEnabled ? <Badge variant="success">Bật</Badge>
            : <Badge variant="secondary">Tổ chức tắt</Badge>}
          {r.platformAiDisabled && <Badge variant="destructive">Platform khoá</Badge>}
        </div>
      ),
    },
    {
      key: 'lock', header: 'Khoá AI',
      cell: (r) => (
        <Button
          size="sm" variant={r.platformAiDisabled ? 'outline' : 'ghost'} className="h-8"
          disabled={setQuota.isPending}
          onClick={() => toggleLock(r)}
        >
          {r.platformAiDisabled ? 'Mở khoá' : 'Khoá'}
        </Button>
      ),
    },
    {
      key: 'quota', header: 'Trần Platform / ngày',
      cell: (r) =>
        editing === r.orgId ? (
          <div className="flex items-center gap-1.5">
            <Input
              type="number" min={0} value={draft} className="h-8 w-24" placeholder="Không đặt"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(r.orgId) }}
              autoFocus
            />
            <Button size="sm" className="h-8" disabled={setQuota.isPending} onClick={() => save(r.orgId)}>Lưu</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}>Huỷ</Button>
          </div>
        ) : (
          <div className="space-y-0.5">
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-sm underline-offset-2 hover:bg-muted hover:underline"
              onClick={() => { setEditing(r.orgId); setDraft(r.platformMaxDaily != null ? String(r.platformMaxDaily) : '') }}
              title="Bấm để đặt trần lượt gọi AI mỗi ngày do Platform áp (tổ chức không sửa được)"
            >
              {r.platformMaxDaily != null ? formatNumber(r.platformMaxDaily) : 'Không đặt'}
            </button>
            {r.maxDaily != null && <p className="px-1.5 text-[11px] text-muted-foreground">Tổ chức tự đặt: {formatNumber(r.maxDaily)}</p>}
          </div>
        ),
    },
  ]

  const t = data?.totals

  return (
    <div className="space-y-4">
      <PageHeader
        title="Usage & chi phí AI"
        description="Chi phí AI theo từng tổ chức. Khoá AI và trần lượt / ngày ở đây do Platform áp — tổ chức không sửa được; trần thực tế là mức thấp hơn giữa trần Platform và trần tổ chức tự đặt."
      />

      <div className="flex items-center gap-3">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 ngày qua</SelectItem>
            <SelectItem value="30">30 ngày qua</SelectItem>
            <SelectItem value="90">90 ngày qua</SelectItem>
            <SelectItem value="365">1 năm qua</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {t && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Tổng chi phí" value={usd(t.costUsd)} />
          <StatCard label="Lượt gọi AI" value={formatNumber(t.calls)} />
          <StatCard label="Token vào" value={formatNumber(t.tokensIn)} />
          <StatCard label="Token ra" value={formatNumber(t.tokensOut)} />
        </div>
      )}

      {isError ? (
        <ErrorState />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.orgId}
          loading={isLoading}
          emptyTitle="Chưa có lượt dùng AI nào trong kỳ"
        />
      )}
    </div>
  )
}
